import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { mapDtoStatus } from '@epicuro-next/data/baskets-grouping';
import { createCheckOrderResult } from '@epicuro-next/data/baskets-update';
import { UsersApiService } from '@epicuro-next/data/users';
import { selectDiscountFormatRule } from '@epicuro-next/platform/configuration';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import { go, selectNullishQueryParams } from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { toggleWaitingNewOrder } from '@epicuro-next/state/user-ui-state';
import { DigitsInfo, isNonNullish, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { BasketStatus } from 'graphql-api/baskets-api-interfaces';
import { EMPTY, NEVER, of, race, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { CBasketService } from '../../services/c-basket.service';
import { isWaitingForReview } from '../../shared/basket-review-utils/basket-review-utils';
import * as basketActions from '../actions';
import { submitBasketSuccess } from '../actions';
import {
  selectActiveBasketId,
  selectAllBasketManualDiscounts,
  selectBasketData,
  selectBasketDtoFromEntitiesById,
  selectCheckOrderExpiryDuration,
  selectIsBasketChecked,
  selectOpenedBasketId,
  selectOpenedDtoBasketETag,
  selectUIValidations,
} from '../selectors';

/**
 * Basket state side effects
 */
@Injectable()
export class BasketOperationsEffects {
  public validateBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.validateBasket),
      switchMap(() => this.store.pipe(select(selectUIValidations), take(1))),
      filter(isNullish),
      map(() => basketActions.checkBasket()),
    ),
  );

  public checkBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.checkBasket),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      switchMap(([_, basketId]) =>
        race(
          this.cbasketService.checkBasket(basketId).pipe(
            switchMap((dtoBasket) => {
              const validationResults = createCheckOrderResult(dtoBasket);

              return [
                basketActions.checkBasketSuccess({ validationResults }),
                basketActions.loadDtoBasketSuccess({ basket: dtoBasket }),
              ];
            }),
            catchError((error) => [
              basketActions.checkBasketFailure({ error: error.message }),
              showSnackBarMessage({
                message: $localize`:Basket|Failed to validate basket message@@BasketValidateFailure:Failed to validate basket.`,
                messageType: MessageType.DANGER,
              }),
            ]),
          ),
          this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            map(() =>
              basketActions.checkBasketFailure({
                error: 'Canceled by navigation',
              }),
            ),
            take(1),
          ),
        ),
      ),
    ),
  );

  public submitBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.submitBasket),
      concatLatestFrom(() => [
        this.store.pipe(select(selectOpenedBasketId)),
        this.store.pipe(select(selectBasketData)),
        this.store.pipe(select(selectNullishQueryParams)),
      ]),
      switchMap(([_, basketId, basketData, queryParams]) =>
        this.cbasketService.submitBasket(basketId).pipe(
          switchMap(() => [
            go({
              commands: ['basket', basketId, 'success'],
              extras: {
                state: {
                  basket: {
                    ...basketData,
                    status: BasketStatus.Submitted,
                  },
                  forceLeaveBasket: true,
                },
                queryParams,
                queryParamsHandling: 'merge',
              },
            }),
            basketActions.submitBasketSuccess({ basketId }),
            basketActions.resetBasket(),
            toggleWaitingNewOrder({ waitingNewOrder: true }),
          ]),
          catchError((error) => [
            basketActions.submitBasketFailure({ error: error.message }),
            showSnackBarMessage({
              message: $localize`:Basket|Failed to place basket message@@BasketPlaceFailure:Failed to place basket.`,
              messageType: MessageType.DANGER,
            }),
          ]),
        ),
      ),
    ),
  );

  public submitBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitBasketSuccess),
      concatLatestFrom(() => [this.store.select(selectOpenedDtoBasketETag)]),
      switchMap(([{ basketId }, etag]) => {
        return this.cbasketService.pollOnceDtoBasket(basketId, etag).pipe(
          filter(isNonNullish),
          map((basketDto) =>
            basketActions.loadDtoBasketSuccess({ basket: basketDto }),
          ),
          catchError((err) => {
            console.warn(err);
            return EMPTY;
          }),
        );
      }),
    ),
  );

  public resetBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.resetBasket),
      map(() => basketActions.updateLastUsedBasketId({ basketId: '' })),
    ),
  );

  public updateLastUsedActiveBasketId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateLastUsedBasketId),
      concatLatestFrom(() =>
        this.store.pipe(
          select(selectActiveAccount),
          filter(Boolean),
          map(({ accountId }) => accountId),
        ),
      ),
      switchMap(([{ basketId }, accountId]) =>
        this.userService.setActiveBasketId(accountId, basketId).pipe(
          withLatestFrom(this.store.pipe(select(selectActiveBasketId))),
          filter(([_, activeBasketId]) => activeBasketId !== basketId),
          map(() => basketActions.setActiveBasketId({ basketId })),
          catchError((error) =>
            of(
              basketActions.setActiveBasketIdFailure({ error: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  public switchDtoBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.switchBasket),
      switchMap(({ basketId }) => {
        return this.cbasketService.getDtoBasket(basketId).pipe(
          switchMap((basket) => [
            basketActions.loadDtoBasketSuccess({ basket }),
            basketActions.switchBasketSuccess({ basketId: basket.basketId }),
          ]),
          catchError((error: HttpErrorResponse) => [
            basketActions.switchBasketFailure({
              status: error.status,
              message: error.message,
            }),
            basketActions.loadDtoBasketFailure({ error: error.message }),
          ]),
        );
      }),
    ),
  );

  public switchBasketFailureGeneral$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.switchBasketFailure),
      filter(({ status }) => status !== 403),
      map(() => {
        return showSnackBarMessage({
          message: $localize`:Basket|Failed to switch basket message@@BasketSwitchFail:Failed to switch basket.`,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public switchBasketFailureAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.switchBasketFailure),
      filter(({ status }) => status === 403),
      map(() => {
        return showSnackBarMessage({
          message: $localize`:Basket|Auth failed to switch basket message@@BasketSwitchAuthFail:Failed to switch basket due to access restriction.`,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public changeActiveBasketOnSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.switchBasketSuccess, basketActions.makeBasketActive),
      switchMap(({ basketId }) =>
        this.store.pipe(
          select(selectBasketDtoFromEntitiesById(basketId)),
          filter(isNonNullish),
          take(1),
        ),
      ),
      filter(
        (basket) =>
          mapDtoStatus(basket.status) === BasketStatus.New &&
          !isWaitingForReview(basket) &&
          !basket.isReadOnly,
      ),
      switchMap((basket) => [
        basketActions.setActiveBasketId({ basketId: basket.basketId }),
        basketActions.updateLastUsedBasketId({ basketId: basket.basketId }),
        basketActions.updateActiveBasketAccounts(),
      ]),
    ),
  );

  public resetOpenedBasketOnMakeActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.makeBasketActive),
      map(() => basketActions.resetOpenedBasket()),
    ),
  );

  public goToSwitchedBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.switchBasketSuccess),
      concatLatestFrom(() => this.store.pipe(select(selectNullishQueryParams))),
      switchMap(([{ basketId }, queryParams]) => [
        go({
          commands: [`basket/${basketId}`],
          extras: {
            state: { forceLeaveBasket: true },
            queryParams,
            queryParamsHandling: 'merge',
          },
        }),
        toggleWaitingNewOrder({ waitingNewOrder: true }),
      ]),
    ),
  );

  public deleteBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.deleteBasket),
      mergeMap(({ basketId }) => {
        return this.cbasketService.deleteBasket(basketId).pipe(
          switchMap(() => [
            basketActions.deleteBasketSuccess({ basketId }),
            basketActions.clearBasketManualDiscounts({ basketId }),
            showSnackBarMessage({
              message: $localize`:Basket|Success to delete basket message@@basketDeleteSuccess:Basket deleted successfully`,
              messageType: MessageType.SUCCESS,
            }),
          ]),
          catchError(() => [
            basketActions.deleteBasketFailure(),
            showSnackBarMessage({
              message: $localize`:Basket|Failed to delete basket message@@basketDeleteFailure:Failed to delete basket.`,
              messageType: MessageType.DANGER,
            }),
          ]),
        );
      }),
    ),
  );

  public resetActiveBasketOnDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.deleteBasket),
      concatLatestFrom(() => this.store.pipe(select(selectActiveBasketId))),
      switchMap(([{ basketId: basketIdToDelete }, activeBasketId]) =>
        basketIdToDelete === activeBasketId
          ? [basketActions.resetBasket()]
          : NEVER,
      ),
    ),
  );

  public resetBasketCheckOrderExpiryWhenExpired$ = createEffect(() =>
    this.store.pipe(
      select(selectIsBasketChecked),
      switchMap((isChecked) => {
        if (isChecked) {
          return this.store.pipe(
            select(selectCheckOrderExpiryDuration),
            take(1),
            switchMap((duration) => timer(duration.milliseconds)),
          );
        }

        return NEVER;
      }),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      map(([_, basketId]) =>
        basketActions.resetBasketCheckedStatus({ basketId }),
      ),
    ),
  );

  public deleteBasketManualDiscountsAtServer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.removeBasketManualDiscounts),
      switchMap(({ basketId, basketItemIds }) => {
        this.store.dispatch(
          basketActions.manualDiscountsBusyDeleting({ basketId }),
        );

        return this.cbasketService
          .deleteManualDiscounts(basketId, basketItemIds)
          .pipe(
            switchMap((etag) => {
              return [
                basketActions.manualDiscountsDeleteSuccess({ basketId }),
                basketActions.updateBasketEtag({ basketId, etag }),
              ];
            }),
            catchError((error) => [
              basketActions.manualDiscountsDeleteFailed({
                basketId,
                basketItemIds,
                message: error.message,
              }),
            ]),
          );
      }),
    ),
  );

  public updateBasketManualDiscountsAtServer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.applyBasketManualDiscountsValidated),
      concatLatestFrom(() => [
        this.store.select(selectAllBasketManualDiscounts),
        this.store.select(selectDiscountFormatRule),
      ]),
      filter(([allManualDiscounts]) => allManualDiscounts !== undefined),
      map(([action, allManualDiscounts, discountFormat]) => {
        const basketId = action.basketId;

        const manualDiscounts = allManualDiscounts.filter(
          (e) => e.basketId === basketId,
        );

        const payload = manualDiscounts
          .filter((entity) => entity.currentValue !== null)
          .map((entity) => {
            return {
              basketItemId: entity.basketItemId,
              value: this.transformDiscountValue(
                entity.currentValue as number,
                discountFormat,
              ),
            };
          });

        return {
          payload,
          action,
          basketId,
        };
      }),
      switchMap(({ payload, basketId }) => {
        this.store.dispatch(
          basketActions.manualDiscountsBusyUpdating({ basketId }),
        );

        const basketItemIds = payload.map((e) => e.basketItemId);

        return this.cbasketService
          .updateManualDiscounts(basketId, payload)
          .pipe(
            switchMap((etag) => {
              return [
                basketActions.manualDiscountsUpdatedSuccess({ basketId }),
                basketActions.updateBasketEtag({ basketId, etag }),
              ];
            }),
            catchError((error) => [
              basketActions.manualDiscountsUpdatedFailed({
                basketId,
                basketItemIds,
                message: error.message,
              }),
            ]),
          );
      }),
    ),
  );

  private transformDiscountValue(
    manualDiscount: number,
    discountFormat: string,
  ): number {
    // The user enters in percentages, but BE wants a factor.
    // So the user might have entered 35.26, and to BE it needs to be send as 0.3526
    // Consequently the discount format, if e.g. 1.0-2 should then become 1.0-4.
    // Hence this transformation of digits info.
    const digitsInfo = (
      DigitsInfo.fromString(discountFormat) ?? new DigitsInfo(1, 0, 2)
    )
      .transform({ maxFractionDigitsToAdd: 2 })
      .toString();

    return Number(this.decimalPipe.transform(manualDiscount, digitsInfo));
  }

  /** Dependencies
   * @param actions$
   * @param cbasketService
   * @param store
   * @param userService
   */
  constructor(
    private actions$: Actions,
    private cbasketService: CBasketService,
    private readonly decimalPipe: DecimalPipe,
    private store: Store,
    private userService: UsersApiService,
  ) {}
}
